import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Employee, Department, SavedList, MessageHistory as MessageHistoryType } from './types';
import { MainView, SavedLists, MessageHistory } from './components';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Login, ProtectedRoute } from './components/auth';
import axiosInstance from './utils/axios';
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Alert,
  Snackbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

function AppContent() {
  const { isAuthenticated, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Map<string, Department>>(new Map());
  const [savedLists, setSavedLists] = useState<SavedList[]>([]);
  const [messageHistory, setMessageHistory] = useState<MessageHistoryType[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navigationItems = [
    { text: 'Home', path: '/' },
    { text: 'Saved Lists', path: '/saved-lists' },
    { text: 'History', path: '/history' },
  ];

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation" onClick={handleDrawerToggle}>
      <List>
        {navigationItems.map((item) => (
          <ListItem 
            button 
            key={item.text} 
            component={Link} 
            to={item.path}
            sx={{ py: 2 }}
          >
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
        <ListItem 
          button 
          onClick={logout}
          sx={{ py: 2 }}
        >
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  // Fetch initial data
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [employeesRes, listsRes, historyRes] = await Promise.all([
        axiosInstance.get<Employee[]>('/employees'),
        axiosInstance.get<SavedList[]>('/recipient-lists'),
        axiosInstance.get<MessageHistoryType[]>('/message-history')
      ]);

      setEmployees(employeesRes.data);
      setSavedLists(listsRes.data);
      setMessageHistory(historyRes.data);

      // Build departments map
      const deptMap = new Map<string, Department>();
      employeesRes.data.forEach((emp: Employee) => {
        if (emp.department) {
          const dept = deptMap.get(emp.department) || { name: emp.department, employeeCount: 0 };
          dept.employeeCount++;
          deptMap.set(emp.department, dept);
        }
      });
      setDepartments(deptMap);
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<'name' | 'department' | 'phone'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  return (
    <>
      {isAuthenticated && (
        <>
          <AppBar position="static">
            <Container maxWidth="lg">
              <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', py: 2 }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: 'inherit',
                  }}
                >
                  Emergency SMS System
                </Typography>
                {isMobile ? (
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="end"
                    onClick={handleDrawerToggle}
                  >
                    <MenuIcon />
                  </IconButton>
                ) : (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {navigationItems.map((item) => (
                      <Button 
                        key={item.text}
                        color="inherit" 
                        component={Link} 
                        to={item.path}
                        sx={{ px: 2 }}
                      >
                        {item.text}
                      </Button>
                    ))}
                    <Button 
                      color="inherit" 
                      onClick={logout}
                      sx={{ px: 2 }}
                    >
                      Logout
                    </Button>
                  </Box>
                )}
              </Toolbar>
            </Container>
          </AppBar>
          <Drawer
            variant="temporary"
            anchor="right"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
            }}
          >
            {drawer}
          </Drawer>
        </>
      )}

      <Routes>
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        } />
        <Route path="/" element={
          <Container maxWidth="lg" sx={{ py: 2 }}>
            <ProtectedRoute>
              <MainView
                employees={employees}
                departments={departments}
                selectedDepartments={selectedDepartments}
                selectedEmployees={selectedEmployees}
                searchQuery={searchQuery}
                sortField={sortField}
                sortOrder={sortOrder}
                loading={loading}
                onSendMessage={async (message: string) => {
                  try {
                    setLoading(true);
                    const response = await axiosInstance.post('/send-sms', {
                      recipients: selectedEmployees,
                      message
                    });
                    setSuccess('Message sending started');
                    // Refresh message history
                    const historyRes = await axiosInstance.get<MessageHistoryType[]>('/message-history');
                    setMessageHistory(historyRes.data);
                    return response.data;
                  } catch (err) {
                    setError('Failed to send message');
                    console.error('Error sending message:', err);
                    throw err;
                  } finally {
                    setLoading(false);
                  }
                }}
                onDepartmentSelect={(deptName: string) => {
                  const isSelected = selectedDepartments.includes(deptName);
                  if (!isSelected) {
                    const deptEmployees = employees
                      .filter(emp => emp.department === deptName)
                      .map(emp => emp.id);
                    setSelectedEmployees(prev => Array.from(new Set([...prev, ...deptEmployees])));
                  } else {
                    setSelectedEmployees(prev => prev.filter(id => {
                      const employee = employees.find(emp => emp.id === id);
                      return employee && employee.department !== deptName;
                    }));
                  }
                  setSelectedDepartments(prev => 
                    isSelected ? prev.filter(d => d !== deptName) : [...prev, deptName]
                  );
                }}
                onEmployeeSelect={(employeeId: string) => {
                  setSelectedEmployees(prev => 
                    prev.includes(employeeId)
                      ? prev.filter(id => id !== employeeId)
                      : [...prev, employeeId]
                  );
                }}
                onSearchChange={setSearchQuery}
                onSort={(field: 'name' | 'department' | 'phone') => {
                  setSortOrder(currentOrder => field === sortField && currentOrder === 'asc' ? 'desc' : 'asc');
                  setSortField(field);
                }}
                onSelectAllEmployees={(checked: boolean) => 
                  setSelectedEmployees(checked ? employees.map(emp => emp.id) : [])
                }
                onSaveListClick={() => {
                  if (selectedEmployees.length === 0) {
                    setError('Please select employees to save');
                    return;
                  }
                  // Handle save list logic in parent component
                  const listName = prompt('Enter list name:');
                  if (listName) {
                    axiosInstance.post('/recipient-lists', {
                      name: listName,
                      recipients: selectedEmployees
                    })
                    .then(() => {
                      setSuccess('List saved successfully');
                      return axiosInstance.get<SavedList[]>('/recipient-lists');
                    })
                    .then((res) => {
                      setSavedLists(res.data);
                    })
                    .catch(() => {
                      setError('Failed to save list');
                    });
                  }
                }}
              />
            </ProtectedRoute>
          </Container>
        } />
        <Route path="/saved-lists" element={
          <Container maxWidth="lg" sx={{ py: 2 }}>
            <ProtectedRoute>
              <SavedLists
                lists={savedLists}
                onLoadList={(list) => {
                  setSelectedEmployees(list.recipients);
                  setSuccess(`Loaded list: ${list.name}`);
                }}
                onDeleteList={async (listId) => {
                  try {
                    setLoading(true);
                    await axiosInstance.delete(`/recipient-lists/${listId}`);
                    setSavedLists(prev => prev.filter(list => list.id !== listId));
                    setSuccess('List deleted successfully');
                  } catch (err) {
                    setError('Failed to delete list');
                  } finally {
                    setLoading(false);
                  }
                }}
              />
            </ProtectedRoute>
          </Container>
        } />
        <Route path="/history" element={
          <Container maxWidth="lg" sx={{ py: 2 }}>
            <ProtectedRoute>
              <MessageHistory
                messages={messageHistory}
                loading={loading}
              />
            </ProtectedRoute>
          </Container>
        } />
      </Routes>

      <Snackbar
        open={Boolean(error || success)}
        autoHideDuration={3000}
        onClose={() => {
          setError('');
          setSuccess('');
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => {
            setError('');
            setSuccess('');
          }}
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
